<script setup lang="ts">
import { MMIcon, MMText } from "@mm-frontend/mm-web-ui-kit";
import { ESearchSuggestionBlockType, type TSearchSuggestionBlock } from "../models/TSearchSuggestions";
import { ESearchSuggestionItemType } from "../models/TSuggestionItem";
import SuggestionItem from "./SuggestionItem.vue";
import { onMounted, computed, watch } from 'vue';
import { useSuggestTracker } from '../trackers/useSuggestTracker';
import { ButtonTertiaryGhost } from "~/features/ui-kit";
import { useSearchHistoryStore } from "~/features/store/search-history";
import { useAppSearch } from '../business/useAppSearch';

const props = defineProps<{
  block: TSearchSuggestionBlock;
}>();

const suggestType = computed(() => {
  switch (props.block.blockType) {
    case 'searched': return 'searchHistory';
    case 'text': return 'suggest';
    case 'popular': return 'suggest';
    case 'category': return 'categorySuggest';
    case 'shop': return 'shopSuggest';
    default: return 'suggest';
  }
});

const { trackView, trackClick } = useSuggestTracker(suggestType.value);
const { query } = useAppSearch();

function sendSuggestView() {
  if (!suggestType.value) return;
  (props.block.items as any[]).forEach((item, index) => {
    const position = index.toString();
    const extra = { position, search_text: query.value } as any;

    if (suggestType.value === 'searchHistory') {
      extra.is_previous = true;
      extra.title = item;
    } else if (suggestType.value === 'suggest') {
      extra.title = typeof item === 'string' ? item : item.title;
    } else if (suggestType.value === 'categorySuggest') {
      extra.category_name = item.title;
    } else if (suggestType.value === 'shopSuggest') {
      // only search_text
    }

    trackView(extra);
  });
}

onMounted(() => {
  sendSuggestView();
});

const signature = computed(() => {
  const normItems = (props.block.items as any[]).map((it: any) =>
    typeof it === 'string' ? it : (it?.title ?? '')
  );
  // Важно: не включаем query в подпись, чтобы не отправлять view при каждом изменении текста
  return JSON.stringify({ t: suggestType.value, i: normItems });
});

watch(signature, (curr, prev) => {
  if (curr !== prev) {
    sendSuggestView();
  }
});

const { clearSearchHistory, removeSearchHistoryValue } = useSearchHistoryStore();
</script>

<template>
  <div class="suggestion-block">
    <template v-if="block.blockType === ESearchSuggestionBlockType.text">
      <SuggestionItem 
        v-for="(item, index) in block.items" 
        :key="index"
        :type="ESearchSuggestionItemType.text"
        :title="item.title"
        :url="`/search?query=${item.title}`"
        class="suggestion-block__item"
        @click="trackClick({ search_text: query, title: item.title, position: index.toString() })"
      />
    </template>

    <template v-else-if="block.blockType === ESearchSuggestionBlockType.popular && block.items.length > 0">
      <SuggestionItem 
        v-for="(item, index) in block.items" 
        :key="index"
        :type="ESearchSuggestionItemType.text"
        :title="item"
        :url="`/search?query=${item}`"
        class="suggestion-block__item"
        @click="trackClick({ search_text: query, title: item, position: index.toString() })"
      />
    </template>

    <template v-else-if="block.blockType === ESearchSuggestionBlockType.category && block.items.length > 0">
      <SuggestionItem 
        v-for="(item, index) in block.items" 
        :key="index"
        :type="ESearchSuggestionItemType.category"
        :title="item.title"
        :url="item.url"
        :subtitle="item.parent?.title"
        class="suggestion-block__item"
        @click="trackClick({ search_text: query, category_name: item.title, position: index.toString() })"
      />
    </template>

    <template v-else-if="block.blockType === ESearchSuggestionBlockType.shop && block.items.length > 0">
      <SuggestionItem 
        v-for="(item, index) in block.items" 
        :key="index"
        :type="ESearchSuggestionItemType.shop"
        :title="item.title"
        :url="`/${item.url}`"
        subtitle="Продавец"
        :image="item.avatar.low"
        class="suggestion-block__item"
        @click="trackClick({ search_text: query })"
      />
    </template>

    <template v-else-if="block.blockType === ESearchSuggestionBlockType.searched && block.items.length > 0">
      <div class="suggestion-block__header">
        <MMText label="Вы недавно искали" font="Body L/Medium" class="suggestion-block__title" />
        <ButtonTertiaryGhost
          class="suggestion-block__clear"
          @click="clearSearchHistory"
        >
          <MMText label="Очистить" font="Body S/Medium" />
        </ButtonTertiaryGhost>
      </div>
      
      <div v-for="(item, index) in block.items" :key="index" class="suggestion-block__item-wrapper--history">
        <SuggestionItem
          :type="ESearchSuggestionItemType.text"
          :title="item"
          :url="`/search?query=${item}`"
          class="suggestion-block__item"
          icon="watch"
          @click="trackClick({ title: item, is_previous: true, position: index.toString() })"
        />

        <div class="suggestion-block__item-wrapper--history__icon" @click="removeSearchHistoryValue(item)">
          <MMIcon name="dismiss-filled" :width="14" :height="14" />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="stylus">
.suggestion-block
  overflow hidden
  background-color var(--background-primary)
  border-radius 10px

  @media (max-width: $pre-laptop)
    border-radius 16px

    &:first-child
      border-top-left-radius 0
      border-top-right-radius 0

  &__header
    display flex
    justify-content space-between
    align-items center
    padding 16px 16px 8px 16px

  &__item-wrapper--history
    position relative

    &__icon
      width 64px
      height 100%
      flex 1 0 64px
      display flex
      align-items center
      justify-content center
      cursor pointer
      position absolute
      right 0
      top 0
      opacity .2
      transition opacity .2s

      &:hover
        opacity 1
</style>